import type { Group } from '@reliance/types/models/MobileDevice';
import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import api from '../common/api';
import type { IStatusResponse } from '../common/reliance-api-service';

type AddEditModalProps = {
  show: boolean;
  onClose: (saved: boolean) => void;
  facilityId: number;
  group: Group | null;
};

const AddEditModal: React.FC<AddEditModalProps> = ({ show, onClose, facilityId, group }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setName(group?.name ?? '');
      setError(null);
    }
  }, [show, group]);

  const handleClose = () => {
    if (!saving) {
      onClose(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (group) {
        const res = await api.put<IStatusResponse>(`/mobile-device/group/${group.id}`, { name });
        if (!res.data?.success) {
          setError('An unexpected error occurred');
          return;
        }
      } else {
        const res = await api.post<IStatusResponse>('/mobile-device/group', { name, facilityId });
        if (!res.data?.success) {
          setError('An unexpected error occurred');
          return;
        }
      }
      onClose(true);
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } };
      if (ax.response?.status === 400) {
        setError('Please verify the inputs are valid and try again');
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{group ? 'Edit Group' : 'Add Group'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error != null && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        <Form.Group>
          <Form.Label>Name</Form.Label>
          <Form.Control
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            autoFocus
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditModal;
